import {
  AuthResponse,
  Product,
  Transaction,
  TransactionSummary,
  OrganizationUser,
  StripeConnectStatus,
  CartItem,
  StripeMode,
  InviteLookup,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

class ApiService {
  private token: string | null = null;
  private subdomain: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.subdomain = this.getSubdomain();
  }

  private getSubdomain(): string {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Check if accessing via subdomain (e.g., test3.taplo.cc)
    if (parts.length >= 3 && parts[0] !== 'www') {
      return parts[0];
    }

    // For root domain, localhost, or any other case, use stored subdomain
    return localStorage.getItem('user_subdomain') || '';
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    this.subdomain = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_subdomain');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    opts: { sendSubdomain?: boolean } = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Refresh subdomain before each request
    this.subdomain = this.getSubdomain();

    const sendSubdomain = opts.sendSubdomain ?? true;
    if (sendSubdomain && this.subdomain) {
      headers['X-Tenant-Subdomain'] = this.subdomain;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth endpoints
  async register(data: {
    email: string;
    password: string;
    fullName: string;
    organizationName?: string;
    subdomain?: string;
    inviteToken: string;
  }): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      { sendSubdomain: false }
    );
    this.setToken(result.token);
    this.subdomain = result.organization.subdomain;
    localStorage.setItem('user_subdomain', this.subdomain);
    return result;
  }

  async lookupInvite(token: string): Promise<InviteLookup> {
    return this.request<InviteLookup>(
      `/auth/invites/${encodeURIComponent(token)}`,
      {},
      { sendSubdomain: false }
    );
  }

  async login(email: string, password: string, subdomain?: string): Promise<AuthResponse> {
    const sub = subdomain || this.subdomain;
    const result = await this.request<AuthResponse>(
      `/auth/login?subdomain=${sub}`,
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }
    );
    this.setToken(result.token);
    this.subdomain = result.organization.subdomain;
    localStorage.setItem('user_subdomain', this.subdomain);
    return result;
  }

  async getMe(): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/me');
  }

  // Product endpoints
  async getProducts(showInactive = false): Promise<{ products: Product[]; categoryOrder?: string[] }> {
    return this.request(`/api/products?showInactive=${showInactive}`);
  }

  async updateCategoryOrder(order: string[]): Promise<{ success: boolean; categoryOrder: string[] }> {
    return this.request('/api/organizations/category-order', {
      method: 'PUT',
      body: JSON.stringify({ order }),
    });
  }

  async getProduct(id: string): Promise<{ product: Product }> {
    return this.request(`/api/products/${id}`);
  }

  async createProduct(data: Partial<Product>): Promise<{ product: Product }> {
    return this.request('/api/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<{ product: Product }> {
    return this.request(`/api/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/products/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadProductImage(file: File): Promise<{ url: string }> {
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    this.subdomain = this.getSubdomain();
    if (this.subdomain) headers['X-Tenant-Subdomain'] = this.subdomain;

    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/api/uploads/image`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Terminal endpoints
  async createCheckoutSession(data: {
    items: CartItem[];
    tipAmount?: number;
    customerEmail?: string;
  }): Promise<{
    checkoutSession: { id: string; url: string; expiresAt: number | null };
    transaction: { id: string; subtotal: number; tax: number; tip: number; total: number };
  }> {
    return this.request('/api/terminal/checkout-session', {
      method: 'POST',
      body: JSON.stringify({ ...data, originUrl: window.location.origin }),
    });
  }

  async cancelCheckoutSession(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/terminal/checkout-session/${id}/cancel`, {
      method: 'POST',
    });
  }

  async getTransactions(params: {
    limit?: number;
    offset?: number;
    status?: string;
    from?: number;
    to?: number;
  } = {}): Promise<{ transactions: Transaction[]; total: number }> {
    const { limit = 25, offset = 0, status = '', from = 0, to = Date.now() } = params;
    const q = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      from: String(from),
      to: String(to),
      ...(status ? { status } : {}),
    });
    return this.request(`/api/terminal/transactions?${q}`);
  }

  async getTransactionsSummary(params: { from?: number; to?: number } = {}): Promise<TransactionSummary> {
    const { from = 0, to = Date.now() } = params;
    return this.request(`/api/terminal/transactions/summary?from=${from}&to=${to}`);
  }

  async getTransactionStatus(transactionId: string): Promise<{
    id: string;
    status: 'pending' | 'succeeded' | 'failed' | 'canceled';
    paymentIntentId: string;
    updatedAt: number;
  }> {
    return this.request(`/api/terminal/transactions/${transactionId}/status`);
  }

  // Stripe Connect endpoints (mode-aware)
  async initiateStripeOnboarding(mode: StripeMode): Promise<{ url: string; accountId: string; mode: StripeMode }> {
    return this.request(`/api/stripe/onboard?mode=${mode}`, {
      method: 'POST',
    });
  }

  async getStripeConnectStatus(): Promise<StripeConnectStatus> {
    return this.request('/api/stripe/status');
  }

  async getStripeDashboardLink(mode: StripeMode): Promise<{ url: string }> {
    return this.request(`/api/stripe/dashboard?mode=${mode}`, {
      method: 'POST',
    });
  }

  async disconnectStripeAccount(mode: StripeMode): Promise<{ success: boolean; message: string; mode: StripeMode }> {
    return this.request(`/api/stripe/disconnect?mode=${mode}`, {
      method: 'POST',
    });
  }

  async setStripeMode(mode: StripeMode): Promise<{ success: boolean; mode: StripeMode }> {
    return this.request('/api/stripe/mode', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    });
  }

  // Organization endpoints
  async getOrganizationUsers(): Promise<{ users: OrganizationUser[] }> {
    return this.request('/api/organizations/users');
  }

  async addUser(data: {
    email: string;
    fullName: string;
    password: string;
    role: string;
  }): Promise<{ user: OrganizationUser }> {
    return this.request('/api/organizations/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserRole(userId: string, role: string): Promise<{ success: boolean }> {
    return this.request(`/api/organizations/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async removeUser(userId: string): Promise<{ success: boolean }> {
    return this.request(`/api/organizations/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async getOrganizationSettings(): Promise<any> {
    return this.request('/api/organizations/settings');
  }

  async updateOrganizationSettings(data: any): Promise<{ success: boolean }> {
    return this.request('/api/organizations/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

export const api = new ApiService();
